import {
  QueryClient,
  type Query,
} from '@tanstack/react-query'
import { persistQueryClient } from '@tanstack/query-persist-client-core'
import { createSyncStoragePersister } from '@tanstack/query-sync-storage-persister'
import { authStore } from './authStore'

/**
 * Retry value matching `@tanstack/react-query`'s `RetryValue<TError>` shape.
 * Re-declared locally because the type is not exported from the package.
 */
export type RetryValue =
  | boolean
  | number
  | ((failureCount: number, error: unknown) => boolean)

export interface CreatePersistedQueryClientOptions {
  /** localStorage key prefix. Must be unique per app (e.g. 'wehunt-web', 'viltrapport-web'). */
  appKey: string

  /**
   * Cache-buster string. Bump when persisted query shapes change in a non-backwards-compatible way.
   * Cache entries persisted under a different buster value are discarded.
   */
  buster: string

  /**
   * Only queries whose first queryKey segment is in this list get persisted.
   * Keep this list small to bound localStorage usage.
   */
  persistedKeys: ReadonlyArray<string>

  /**
   * Max age of persisted cache in ms. Older entries are discarded on restore.
   * @default 24 * 60 * 60 * 1000 (24h)
   */
  maxAgeMs?: number

  /**
   * Override the default retry behavior.
   *
   * The default retries up to 2 times for *any* error. core-web intentionally
   * does not depend on stream/clubhouse code, so it cannot recognise `ApiError`
   * here. Consumers that need 4xx-skip behavior (don't retry 4xx, retry 5xx
   * up to 2 times) should pass a retry function that inspects the error, e.g.:
   *
   *     retry: (count, err) => {
   *       if (err instanceof ApiError && err.status < 500) return false
   *       return count < 2
   *     }
   */
  retry?: RetryValue
}

const LS_KEY_SUFFIX = '-rq-cache'
const DEFAULT_MAX_AGE_MS = 24 * 60 * 60 * 1000

/** Default retry: up to 2 times for any error. */
const defaultRetry: RetryValue = (failureCount, _error) => failureCount < 2

/**
 * Map from QueryClient instance to its dispose function.
 * Used so {@link disposeQueryClient} can tear down the authStore subscription
 * (and any persister unsubscribe) registered by {@link createPersistedQueryClient}.
 */
const disposers = new WeakMap<QueryClient, () => void>()

function hasWindow(): boolean {
  return typeof window !== 'undefined'
}

function storageKey(appKey: string): string {
  return `${appKey}${LS_KEY_SUFFIX}`
}

function buildClient(retry: RetryValue): QueryClient {
  return new QueryClient({
    defaultOptions: {
      queries: {
        staleTime: 30_000,
        retry,
        refetchOnWindowFocus: false,
      },
      mutations: { retry: false },
      // dehydrate config is honored by persistQueryClient when persistence is enabled.
      dehydrate: {
        shouldDehydrateQuery: () => false,
      },
    },
  })
}

/**
 * Creates a {@link QueryClient} with WeHunt-platform defaults plus optional
 * localStorage-backed persistence and uid-change-aware cache clearing.
 *
 * Persistence:
 *   - Uses {@link createSyncStoragePersister} backed by `window.localStorage`.
 *   - Only queries whose first queryKey segment is in `persistedKeys` AND
 *     whose state is `'success'` are persisted.
 *   - Persisted cache older than `maxAgeMs` is discarded on restore.
 *   - `buster` invalidates the persisted cache when bumped.
 *
 * Cache clearing:
 *   - Subscribes to {@link authStore}. The in-memory cache is cleared via
 *     `queryClient.clear()` and the persisted localStorage entry is removed
 *     whenever the signed-in uid actually changes — either a transition into
 *     `'signed-out'`, or a `'signed-in'` emission carrying a different uid
 *     than the previous `'signed-in'` emission.
 *   - The uid-change case exists because Apple sign-in can perform on-success
 *     identity resolution: after a successful sign-in it may call
 *     `signInWithCustomToken` to swap the session to a *different* Firebase
 *     user, because one person can hold two Firebase users that Firebase
 *     itself cannot link (Apple issues a subject identifier per developer
 *     team, and a Firebase user holds one `apple.com` provider entry). That
 *     swap changes uid without ever passing through `'signed-out'`, so
 *     clearing only on sign-out would let uid A's cached data (persisted copy
 *     included) survive into uid B's session.
 *   - A repeated emission of the *same* signed-in uid (token refresh,
 *     listener re-fire) does not clear — only a changed uid does. The first
 *     `'signed-in'` after `'loading'` also does not clear: there is no
 *     previous user's data to protect at that point, and clearing there would
 *     cost a cold start on every page load for no privacy benefit.
 *
 * SSR safety:
 *   - If `window` is undefined, returns a {@link QueryClient} without
 *     persistence and without sign-out wiring (still applies the defaults).
 *
 * Use {@link disposeQueryClient} to tear down all subscriptions (mainly useful
 * in tests).
 */
export function createPersistedQueryClient(
  opts: CreatePersistedQueryClientOptions,
): QueryClient {
  const retry = opts.retry ?? defaultRetry
  const client = buildClient(retry)

  if (!hasWindow()) {
    // SSR/Node: skip persistence and authStore wiring.
    return client
  }

  const persistedKeys = new Set(opts.persistedKeys)

  // Apply the configured dehydrate filter now that we know we have a window.
  client.setDefaultOptions({
    ...client.getDefaultOptions(),
    dehydrate: {
      shouldDehydrateQuery: (query: Query) => {
        if (query.state.status !== 'success') return false
        const firstSegment = query.queryKey[0]
        if (typeof firstSegment !== 'string') return false
        return persistedKeys.has(firstSegment)
      },
    },
  })

  const key = storageKey(opts.appKey)
  const persister = createSyncStoragePersister({
    storage: window.localStorage,
    key,
  })

  // persistQueryClient (v5) returns [unsubscribe, restorePromise]. We discard
  // the promise — restoration happens asynchronously but consumers do not need
  // to await it (queries hit cached values once hydrated).
  const [unsubscribePersist] = persistQueryClient({
    queryClient: client,
    persister,
    buster: opts.buster,
    maxAge: opts.maxAgeMs ?? DEFAULT_MAX_AGE_MS,
    dehydrateOptions: {
      shouldDehydrateQuery: (query: Query) => {
        if (query.state.status !== 'success') return false
        const firstSegment = query.queryKey[0]
        if (typeof firstSegment !== 'string') return false
        return persistedKeys.has(firstSegment)
      },
    },
  })

  function clearCache(): void {
    client.clear()
    try {
      window.localStorage.removeItem(key)
    } catch {
      // localStorage can throw in restricted contexts; safe to swallow here.
    }
  }

  // Remembers the previous auth state so a uid change can be told apart from
  // a repeated emission of the same signed-in uid (token refresh, listener
  // re-fire) — only a changed uid should clear the cache.
  let previousState = authStore.snapshot()
  const unsubscribeAuth = authStore.subscribe((next) => {
    const wasSignedOut = previousState.status === 'signed-out'
    const uidChanged =
      previousState.status === 'signed-in' &&
      next.status === 'signed-in' &&
      next.user.uid !== previousState.user.uid

    if ((next.status === 'signed-out' && !wasSignedOut) || uidChanged) {
      clearCache()
    }
    previousState = next
  })

  disposers.set(client, () => {
    unsubscribeAuth()
    unsubscribePersist()
  })

  return client
}

/**
 * Tears down the authStore subscription and persister subscription wired up
 * by {@link createPersistedQueryClient}. Safe to call on any QueryClient —
 * no-op if not registered.
 */
export function disposeQueryClient(client: QueryClient): void {
  const dispose = disposers.get(client)
  if (dispose) {
    dispose()
    disposers.delete(client)
  }
}
